'use client'

import { paymentTypeSchema } from '@server/schemas'
import { type Locale, useI18n } from '@shared/config/i18n'
import { dayjs, DEFAULT_TIMEZONE } from '@shared/libs/dayjs'
import React, { memo, useEffect, useState } from 'react'
import { z } from 'zod'

import { Box, HStack, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  title: string
  month?: number
  day?: number
  summaryItems: { label: string; value: string }[]
  paymentType?: z.infer<typeof paymentTypeSchema>
  timezone?: string
}

const DATE_FORMAT_BY_LOCALE = {
  ko: 'YYYY.MM.DD',
  en: 'MMM D, YYYY',
  ja: 'YYYY年M月D日',
} satisfies Record<Locale, string>

const PaymentLectureSummary = memo(
  ({ title, month = 0, day = 0, summaryItems, paymentType, timezone = DEFAULT_TIMEZONE }: Props) => {
    const { effectiveLocale, t } = useI18n()
    // hydration 불일치 방지를 위해 클라이언트에서만 날짜 계산
    const [lecturePeriod, setLecturePeriod] = useState<string>('')

    useEffect(() => {
      const start = dayjs().tz(timezone)
      const end = dayjs(start).add(month, 'month').add(day, 'day')
      const dateFormat = DATE_FORMAT_BY_LOCALE[effectiveLocale]
      setLecturePeriod(
        `${start.locale(effectiveLocale).format(dateFormat)} ~ ${end.locale(effectiveLocale).format(dateFormat)}`,
      )
    }, [day, effectiveLocale, month, timezone])

    return (
      <React.Fragment>
        <VStack className="p-5 bg-gray-100 border border-gray-200 rounded-lg gap-4">
          <VStack className="gap-1">
            <Typography size="h2">{title}</Typography>
            <Typography size="body1" className="text-gray-400">
              {lecturePeriod || t('payment.lecture.calculatingDate')}
            </Typography>
          </VStack>

          <Box className="h-px bg-gray-200" />

          <VStack className="gap-1">
            {summaryItems.map(({ label, value }) => {
              return (
                <HStack className="justify-between" key={label}>
                  <Typography size="body1" className="text-gray-400">
                    {label}
                  </Typography>
                  <Typography size="h6">{value}</Typography>
                </HStack>
              )
            })}
          </VStack>
        </VStack>
        {paymentType === paymentTypeSchema.enum.SUBSCRIBE && (
          <ol className="gap-0.5 list-disc list-outside pl-5 marker:text-gray-400">
            <li>
              <Typography size="h6" className="text-gray-400 font-normal">
                {t('payment.lecture.refundPenaltyNotice')}
              </Typography>
            </li>
          </ol>
        )}
      </React.Fragment>
    )
  },
)

export default PaymentLectureSummary
